import { useState } from "react";
import { useIonRouter } from "@ionic/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { FieldValues, useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "@/components/button";
import { PasswordInput } from "@/components/inputs/password-input";
import { UnauthenticatedPageLayout } from "@/components/layouts/UnauthenticatedPageLayout";
import { customErrorMap } from "@/utils/custom-error-map";
import { request } from "@/utils/request";
import { useSearchParams } from "@/hooks/search-params";
import { useAppContext } from "@/contexts/app-context";

const formSchema = z.object({
  password: z.string().nonempty(),
});

const ResetPasswordPage = () => {
  const { actions: { setAuthToken } } = useAppContext();

  const {
    handleSubmit,
    control,
    formState: { isSubmitting },
  } = useForm<FieldValues>({
    defaultValues: { password: "" },
    resolver: zodResolver(formSchema, { errorMap: customErrorMap }),
  });

  const [error, setError] = useState<string>()

  const { resetPasswordToken } = useSearchParams()
  const router = useIonRouter()

  const onSubmit = async (data: FieldValues) => {
    const response = await request.put<{ token: string }>("/users/passwords", {
      ...data,
      resetPasswordToken,
    });

    if (response.ok && response.data?.token) {
      await setAuthToken(response.data.token);

      router.push("/songs");
    } else if (response.errors) {
      setError(response.errors.base[0]);
    }
  };

  return (
    <UnauthenticatedPageLayout
      title="Reset your password"
      className="max-w-screen-sm w-full mx-auto border rounded-lg bg-gray-100 p-4 flex flex-col gap-4 items-center"
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col gap-4 items-center w-full max-w-md"
      >
        <PasswordInput
          name="password"
          label="New Password"
          control={control}
          placeholder="Create your password"
          hint="Must be at least 8 characters long."
        />

        {
          error && (
            <div className="flex flex-col gap-4 items-center w-full max-w-md">
              <p className="text-red-500">{error}</p>
            </div>
          )
        }

        <Button type="submit" className="btn-primary" loading={isSubmitting}>
          Update Password
        </Button>
      </form>
    </UnauthenticatedPageLayout>
  );
};

export default ResetPasswordPage;
